import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, MapPin, Calendar, Trophy } from "lucide-react";
import OddsCell from "@/components/OddsCell";
import BetSlipDock from "@/components/BetSlipDock";

export default function MatchDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pick, setPick] = useState(null);
  useEffect(() => {
    const load = () =>
      api
        .get(`/cricket/matches/${id}`)
        .then((r) => setData(r.data))
        .catch((e) => setErr(e?.response?.data?.detail || "Match unavailable"));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [id]);

  const m = data?.match;

  return (
    <div className="min-h-screen bg-[#09090D] text-zinc-100">
      <Header />
      <div className="mx-auto max-w-6xl px-4 sm:px-8 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-amber-400 font-display uppercase tracking-widest text-xs mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
        {err && (
          <div className="text-red-400 border border-red-500/30 rounded p-4" data-testid="match-error">
            {err}
          </div>
        )}
        {!m && !err && <div className="animate-pulse h-40 bg-zinc-900 rounded-xl" />}
        {m && (
          <>
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
              <div>
                <div className="text-xs uppercase font-display tracking-widest text-amber-400 flex items-center gap-2">
                  <Trophy className="h-3 w-3" /> {m.competition} · {m.format}
                </div>
                <h1 className="font-display text-3xl sm:text-5xl font-black uppercase mt-2">{m.name}</h1>
                <div className="mt-2 flex items-center gap-4 text-xs text-zinc-400 font-display uppercase tracking-widest">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {m.venue || "TBA"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {m.date ? new Date(m.date).toLocaleString() : "—"}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 font-display uppercase tracking-widest">
                  {m.status}
                </Badge>
                {m.sample && (
                  <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 font-display uppercase tracking-widest">
                    Sample
                  </Badge>
                )}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {(m.teams || []).map((t, i) => (
                <Card key={i} className="bg-zinc-950 border-amber-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-amber-300 to-amber-600 text-black font-display font-black grid place-items-center text-xl">
                        {t.short}
                      </div>
                      <div>
                        <div className="font-display text-2xl font-bold">{t.name}</div>
                        <div className="font-mono-stat text-amber-300 text-lg mt-1">{t.score || "Yet to bat"}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {m.innings?.length > 0 && (
              <section className="mt-10">
                <h2 className="font-display text-2xl font-black uppercase mb-4 gold-text">Scorecard</h2>
                <div className="grid gap-3">
                  {m.innings.map((inn, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-zinc-950 border border-amber-500/15 rounded-lg p-4"
                      data-testid={`innings-${i}`}
                    >
                      <div className="font-display text-lg">{inn.team}</div>
                      <div className="font-mono-stat text-amber-300">
                        {inn.runs}/{inn.wickets} ({inn.overs} ov)
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {m.book?.length > 0 && !data.settled && (
              <section className="mt-10 grid lg:grid-cols-3 gap-6" data-testid="odds-section">
                <div className="lg:col-span-2 bg-zinc-950 border border-amber-500/20 rounded-md overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black px-4 py-2 font-display uppercase tracking-widest font-bold text-sm">
                    Match Odds
                  </div>
                  {m.book.map((o, i) => (
                    <div key={i} className="grid grid-cols-12 items-center gap-2 px-4 py-2 border-b border-zinc-800">
                      <div className="col-span-6 sm:col-span-8 font-semibold">{o.selection}</div>
                      <div className="col-span-3 sm:col-span-2"><OddsCell price={o.back} side="back" testid={`detail-back-${i}`} onPick={() => setPick({ matchId: m.id, match: m.name, selection: o.selection, side: "back", price: o.back })} /></div>
                      <div className="col-span-3 sm:col-span-2"><OddsCell price={o.lay} side="lay" testid={`detail-lay-${i}`} onPick={() => setPick({ matchId: m.id, match: m.name, selection: o.selection, side: "lay", price: o.lay })} /></div>
                    </div>
                  ))}
                </div>
                <div>
                  <BetSlipDock pick={pick} onClear={() => setPick(null)} />
                </div>
              </section>
            )}
            {data.settled && (
              <div className="mt-10 border border-amber-500/30 rounded-md p-4 text-amber-300 font-display uppercase tracking-widest text-sm" data-testid="market-settled">
                Market settled · {data.settled.void ? "Void" : `Winner: ${data.settled.winner}`}
              </div>
            )}

            <div className="mt-10 text-xs text-zinc-500 font-display uppercase tracking-widest">
              Updated {data.updated_at ? new Date(data.updated_at).toLocaleString() : ""}
            </div>
          </>
        )}
      </div>
      <WhatsAppButton />
    </div>
  );
}
